import Device from '../components/Device';
import Image, { Video } from '../components/Image';
import Page from '../components/Page';
import Section from '../components/Section';
import { SITE_ORIGIN, site } from '../content';
import { cx, toDataAttrs } from '../lib/cx';

function Tags({ tags }) {
  return (
    <div className="tags mb-4">
      {tags.map((tag) => (
        <span key={tag} className="tag">{tag}</span>
      ))}
    </div>
  );
}

function CaseHero({ project }) {
  return (
    <div id="case-study">
      <Section id="case-hero" className="section-screen-h theme" pin data={project.hero.theme}>
        <article>
          <div className="contain container-fluid container-limit px-0">
            <div className="content-scroll" {...toDataAttrs(project.hero.scroll)}>
              <div className="contain container">
                <div className="context mb-2">Case Study</div>
                <h1 className="display">{project.title}</h1>
              </div>
            </div>
            <div className="content-fixed" {...toDataAttrs(project.hero.fixed)}>
              <div className="contain container">
                <div className="flex flex-wrap">
                  <div className="w-full md:w-1/2">
                    <p className="intro">{project.intro}</p>
                    <Tags tags={project.caseTags || project.tags} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
        <div className="hero-cover-wrapper">
          <Image src={project.cover.fg || project.cover.bg} className="fg" alt="" />
          <Image src={project.cover.bg} className="bg" alt="" />
          <div className="fade show" />
        </div>
      </Section>
    </div>
  );
}

function CaseIntro({ project }) {
  return (
    <Section
      id="case-intro"
      className="section-screen-h relative theme"
      pin
      data={{
        'data-bg': 'var(--color-brand-primary)',
        'data-color': project.brand.introColor,
        'data-highlight': project.brand.introHighlight,
        'data-highlight-color': 'var(--color-secondary)',
        'data-start': 'complete',
        'data-header-fade': 'false',
      }}
    >
      <div className="content-scroll" {...toDataAttrs(project.introMotion.scroll)}>
        <div className="contain container">
          <h3 className="context">{project.overview.context}</h3>
          <h1>{project.overview.title}</h1>
        </div>
      </div>
      <div className="content-fixed" {...toDataAttrs(project.introMotion.fixed)}>
        <div className="contain container relative z-10">
          <div className="flex flex-wrap">
            <div className="w-full md:w-5/6">
              <div className="pause-placeholder" />
              <p className="intro">{project.overview.body}</p>
            </div>
            <Image className="logo-intro" src={project.overview.logo} alt="" />
          </div>
        </div>
      </div>
      <div className="section-gradient" style={{ '--section-bg': 'var(--color-brand-primary)', '--hue': project.brand.hue }} />
    </Section>
  );
}

function CaseExplain({ project, section }) {
  return (
    <Section
      id={section.id}
      className="section-screen-h relative theme case-explain"
      pin
      data={{
        'data-bg': 'var(--color-brand-primary)',
        'data-color': project.brand.explainColor,
        'data-highlight': project.brand.explainHighlight,
        'data-highlight-color': 'var(--color-secondary)',
        'data-start': 'complete',
        'data-header-fade': 'false',
        ...(section.themeScroll || {}),
      }}
    >
      <div className="content-scroll" {...toDataAttrs(section.scroll)}>
        <div className="contain container">
          <h1 className="display">{section.title}</h1>
        </div>
      </div>
      <div className="content-fixed" {...toDataAttrs(section.fixed)}>
        <div className="contain container">
          <div className="flex flex-wrap">
            <div className="w-full md:w-1/2 md:ml-[50%]">
              {section.insights.map((insight, index) => (
                <div key={insight.title}>
                  {index > 0 ? <div className="divider" style={section.dividerStyle || undefined} /> : null}
                  <div className={`insight insight-${index + 1}`}>
                    <h2 className="h4">{insight.title}</h2>
                    <p>{insight.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="gallery-gradient" style={{ '--section-bg': 'var(--color-brand-primary)' }} />
      <div className={cx('video-wrapper', section.video.className)}>
        <Video src={section.video.src} playbackRate={0.7} />
      </div>
      <div className="pause-placeholder case-display-placeholder" />
    </Section>
  );
}

function OutcomeMedia({ media }) {
  return (
    <div className={media.containerClass}>
      {media.devices.map((device, index) => (
        <Device key={`${device.type}-${index}`} {...device} />
      ))}
    </div>
  );
}

function CaseOutcomes({ project }) {
  return (
    <Section
      id="case-outcomes"
      className="section-screen-h relative theme"
      data={{
        'data-bg': 'var(--color-brand-primary)',
        'data-color': project.brand.explainColor,
        'data-highlight': project.brand.explainHighlight,
        'data-highlight-color': 'var(--color-secondary)',
        'data-start': 'early',
        'data-header-fade': 'false',
      }}
    >
      <div className="content-pad-x relative z-10">
        <div className="contain container">
          {project.outcomes.map((outcome, index) => (
            <div
              key={outcome.index}
              className={cx('row', `row-${index + 1}`, outcome.reverse && 'reverse')}
              {...toDataAttrs(outcome.scroll)}
            >
              <div className={cx(outcome.textClass)}>
                <div className="item content-visible">
                  <div className="context mb-1">{outcome.index}</div>
                  <h2 className="mb-3">{outcome.title}</h2>
                  <p>{outcome.body}</p>
                </div>
              </div>
              <div className={cx(outcome.mediaClass)}>
                <OutcomeMedia media={outcome.media} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="section-gradient" style={{ '--section-bg': 'var(--color-brand-primary)', '--hue': project.brand.hue }} />
    </Section>
  );
}

export default function PageCaseStudy({ project }) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: project.title,
    description: project.seo.description,
    url: `${SITE_ORIGIN}${project.path}`,
    author: {
      '@type': 'Person',
      name: site.person,
      url: SITE_ORIGIN,
    },
  };

  return (
    <Page
      seo={{
        ...project.seo,
        path: project.path,
        image: project.cover.bg,
        type: 'article',
      }}
      jsonLd={jsonLd}
      brand={project.brand}
      headerFade
      contactIntro={site.contact.caseIntro}
    >
      <CaseHero project={project} />
      <CaseIntro project={project} />
      {project.explain.map((section) => (
        <CaseExplain key={section.id} project={project} section={section} />
      ))}
      <CaseOutcomes project={project} />
    </Page>
  );
}
